(() => {
  'use strict';

  /* ==========================================================
     LE BON COLLAGÈNE — PRODUCT FINDER ENGINE
     Moteur de scoring uniquement.
     L'interface sera branchée ultérieurement.
     ========================================================== */

  const SCORES = Object.freeze({
    MAIN_GOAL: 14,
    PRIMARY_SUBGOAL: 8,
    SECONDARY_SUBGOAL: 3,
    SECONDARY_GOAL: 4,

    MENOPAUSE: 10,
    MENOPAUSE_GLOBAL: 12,
    MENOPAUSE_WITHOUT_NEED: -4,

    FORMULA: 7,
    FORMULA_PARTIAL: 3,

    EXPERT_ORIGIN: 5,
    EXPERT_PEPTIDES: 6,
    EXPERT_FORMULA: 6,
    EXPERT_FORMULA_PARTIAL: 3,

    ORIGIN_MATCH: 8,
ORIGIN_MISMATCH: -4,

    ACTIVITY_LEVEL: 3,
    SPORT_TYPE: 3,
    JOINT_SPORT_COMPONENT: 6,
    ANTIDOPING: 14,

    PRACTICALITY: 2,

    SUPPLEMENTS_MANY: 2,
    SUPPLEMENTS_SOME: 1,

    HAIR_SPECIFIC: 10,

    /* Utilisé uniquement s'il n'existe aucun produit
       correspondant à l'objectif principal. */
    MAIN_GOAL_MISMATCH: -30
  });


  const GOAL_FIELDS = Object.freeze({
    skin: 'skinGoals',
    hair: 'hairGoals',
    joints: 'jointGoals',
    sport: 'sportGoals',
    aging: 'agingGoals'
  });


  /* ==========================================================
     HELPERS
     ========================================================== */

  function toArray(value) {
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    return [value];
  }


  function includes(list, value) {
    return toArray(list).includes(value);
  }


  function isTrue(value) {
    return (
      value === true ||
      value === 'true' ||
      value === 1 ||
      value === '1'
    );
  }


  function intersection(a, b) {
    const listA = toArray(a);
    const listB = toArray(b);

    return listA.filter((item) => listB.includes(item));
  }


  function addScore(result, {
    code,
    label,
    points,
    group = 'other'
  }) {
    result.score += points;

    if (points > 0) {
      result.positiveMatches += 1;
    }

    if (group === 'core') {
      result.coreScore += points;
    }

    result.reasons.push({
      code,
      label,
      points,
      group
    });
  }


  function addMismatch(result, code, label) {
    result.mismatches.push({
      code,
      label
    });
  }


  /* ==========================================================
     NORMALISATION DES RÉPONSES
     ========================================================== */

  function normalizeAnswers(raw = {}) {
    return {
      knowledge: raw.knowledge || null,

      expertPreference:
        raw.expertPreference || null,

      mainGoal:
        raw.mainGoal || null,

      secondaryGoals:
        toArray(raw.secondaryGoals),

      primarySubGoal:
        raw.primarySubGoal || null,

      secondarySubGoals:
        toArray(raw.secondarySubGoals),

      menopauseNeed:
        raw.menopauseNeed || 'none',

      formulaPreference:
        raw.formulaPreference || null,

      originPreference:
        raw.originPreference || null,

      avoidTags:
        toArray(raw.avoidTags),

      activityLevel:
        raw.activityLevel || null,

      sportType:
        raw.sportType || null,

      antidopingPreference:
        raw.antidopingPreference || null,

      practicality:
        raw.practicality || null,

      supplements:
        raw.supplements || null,

      hairFormulaPreference:
        raw.hairFormulaPreference || null
    };
  }


  /* ==========================================================
     SCORING D'UN PRODUIT
     ========================================================== */

  function scoreProduct(product, rawAnswers = {}) {
    const answers = normalizeAnswers(rawAnswers);
    const finder = product.finder || {};

    const result = {
      product,
      score: 0,
      coreScore: 0,
      positiveMatches: 0,

      enabled: finder.enabled !== false,
      excluded: false,
      exclusionReasons: [],

      mainGoalCompatible: false,

      reasons: [],
      mismatches: []
    };


    /* ----------------------------------------------------------
       1. PRODUIT ACTIF
       ---------------------------------------------------------- */

    if (finder.enabled === false) {
      result.excluded = true;
      result.exclusionReasons.push('Produit désactivé dans le Product Finder.');
      return result;
    }


    /* ----------------------------------------------------------
       2. EXCLUSIONS ABSOLUES
       Allergies / ingrédients / origines à éviter
       ---------------------------------------------------------- */

    const incompatibleTags = intersection(
      finder.avoidTags,
      answers.avoidTags
    );

    if (incompatibleTags.length > 0) {
      result.excluded = true;

      incompatibleTags.forEach((tag) => {
        result.exclusionReasons.push(
          `Incompatibilité déclarée : ${tag}`
        );
      });

      return result;
    }


    /* ----------------------------------------------------------
       3. OBJECTIF PRINCIPAL
       ---------------------------------------------------------- */

    if (
      answers.mainGoal &&
      includes(finder.goals, answers.mainGoal)
    ) {
      result.mainGoalCompatible = true;

      addScore(result, {
        code: 'main_goal',
        label: `Objectif principal : ${answers.mainGoal}`,
        points: SCORES.MAIN_GOAL,
        group: 'core'
      });
    } else if (answers.mainGoal) {
      addScore(result, {
        code: 'main_goal_mismatch',
        label: 'Correspondance partielle avec l’objectif principal',
        points: SCORES.MAIN_GOAL_MISMATCH,
        group: 'core'
      });

      addMismatch(
        result,
        'main_goal',
        'Ce produit ne cible pas directement votre objectif principal.'
      );
    }


    /* ----------------------------------------------------------
       4. OBJECTIFS SECONDAIRES
       ---------------------------------------------------------- */

    answers.secondaryGoals.forEach((goal) => {
      if (
        goal !== answers.mainGoal &&
        includes(finder.goals, goal)
      ) {
        addScore(result, {
          code: `secondary_goal_${goal}`,
          label: `Objectif secondaire : ${goal}`,
          points: SCORES.SECONDARY_GOAL,
          group: 'core'
        });
      }
    });


    /* ----------------------------------------------------------
   5. SOUS-OBJECTIFS CLASSÉS PAR PRIORITÉ
   ---------------------------------------------------------- */

const subGoalField =
  GOAL_FIELDS[answers.mainGoal];

const productSubGoals =
  subGoalField
    ? toArray(finder[subGoalField])
    : [];


/*
  Le premier sous-objectif vaut le plus de points.
  Les suivants ont progressivement moins de poids.
*/
const SUBGOAL_RANK_SCORES = [
  8, // priorité n°1
  5, // priorité n°2
  3, // priorité n°3
  2, // priorité n°4
  1  // priorité n°5 et suivantes
];


const rankedSubGoals = [
  answers.primarySubGoal,
  ...toArray(answers.secondarySubGoals)
]
  .filter(Boolean)
  .filter(
    (subGoal, index, list) =>
      list.indexOf(subGoal) === index
  );


rankedSubGoals.forEach((subGoal, index) => {

  if (!productSubGoals.includes(subGoal)) {
    return;
  }

  const points =
    SUBGOAL_RANK_SCORES[index] ??
    SUBGOAL_RANK_SCORES[
      SUBGOAL_RANK_SCORES.length - 1
    ];


  addScore(result, {
    code: `ranked_subgoal_${index + 1}_${subGoal}`,
    label:
      index === 0
        ? `Priorité n°1 : ${subGoal}`
        : `Priorité n°${index + 1} : ${subGoal}`,
    points,
    group: 'core'
  });

});


    /* ----------------------------------------------------------
       6. ARTICULATIONS SOLLICITÉES PAR LE SPORT
       ---------------------------------------------------------- */

    const jointSportProfile =
      answers.mainGoal === 'joints' &&
      (
        answers.primarySubGoal === 'sport_load' ||
        answers.secondarySubGoals.includes('sport_load')
      );

    if (
      jointSportProfile &&
      includes(finder.goals, 'sport')
    ) {
      addScore(result, {
        code: 'joint_sport_component',
        label: 'Profil articulaire avec forte composante sportive',
        points: SCORES.JOINT_SPORT_COMPONENT,
        group: 'context'
      });
    }


    /* ----------------------------------------------------------
       7. MÉNOPAUSE
       ---------------------------------------------------------- */

    const menopauseFit =
      toArray(finder.menopauseFit);

    if (answers.menopauseNeed === 'comfort') {
      if (menopauseFit.includes('comfort')) {
        addScore(result, {
          code: 'menopause_comfort',
          label: 'Confort lié à la ménopause',
          points: SCORES.MENOPAUSE,
          group: 'context'
        });
      }
    }

    else if (answers.menopauseNeed === 'mobility') {
      if (menopauseFit.includes('mobility')) {
        addScore(result, {
          code: 'menopause_mobility',
          label: 'Ménopause + mobilité / articulations',
          points: SCORES.MENOPAUSE,
          group: 'context'
        });
      }
    }

    else if (answers.menopauseNeed === 'global') {
      if (menopauseFit.includes('global')) {
        addScore(result, {
          code: 'menopause_global',
          label: 'Approche globale de la ménopause',
          points: SCORES.MENOPAUSE_GLOBAL,
          group: 'context'
        });
      }
    }

    else if (menopauseFit.length > 0) {
      addScore(result, {
        code: 'menopause_without_need',
        label: 'Positionnement ménopause non recherché',
        points: SCORES.MENOPAUSE_WITHOUT_NEED,
        group: 'penalty'
      });
    }


    /* ----------------------------------------------------------
       8. COMPOSITION
       La préférence finale du questionnaire est prioritaire.
       ---------------------------------------------------------- */

    const formulaStyle =
      finder.formulaStyle || null;

    if (answers.formulaPreference === 'simple') {
      if (formulaStyle === 'simple') {
        addScore(result, {
          code: 'formula_simple',
          label: 'Composition simple recherchée',
          points: SCORES.FORMULA,
          group: 'preference'
        });
      }
    }

    else if (answers.formulaPreference === 'balanced') {
      if (formulaStyle === 'balanced') {
        addScore(result, {
          code: 'formula_balanced',
          label: 'Collagène associé à quelques actifs',
          points: SCORES.FORMULA,
          group: 'preference'
        });
      }

      else if (formulaStyle === 'complete') {
        addScore(result, {
          code: 'formula_balanced_partial',
          label: 'Formule enrichie proche de votre préférence',
          points: SCORES.FORMULA_PARTIAL,
          group: 'preference'
        });
      }
    }

    else if (answers.formulaPreference === 'complete') {
      if (formulaStyle === 'complete') {
        addScore(result, {
          code: 'formula_complete',
          label: 'Formule très complète recherchée',
          points: SCORES.FORMULA,
          group: 'preference'
        });
      }

      else if (formulaStyle === 'balanced') {
        addScore(result, {
          code: 'formula_complete_partial',
          label: 'Formule enrichie partiellement compatible',
          points: SCORES.FORMULA_PARTIAL,
          group: 'preference'
        });
      }
    }


    /* ----------------------------------------------------------
       9. PRÉFÉRENCE TECHNIQUE — EXPERT
       Ne pas doubler une préférence explicitée plus tard.
       ---------------------------------------------------------- */

    if (answers.knowledge === 'expert') {

      const pref = answers.expertPreference;

      if (
        pref === 'marine' &&
        !answers.originPreference &&
        finder.origin === 'marine'
      ) {
        addScore(result, {
          code: 'expert_marine',
          label: 'Préférence technique pour le collagène marin',
          points: SCORES.EXPERT_ORIGIN,
          group: 'preference'
        });
      }


      if (
        pref === 'bovine' &&
        !answers.originPreference &&
        finder.origin === 'bovine'
      ) {
        addScore(result, {
          code: 'expert_bovine',
          label: 'Préférence technique pour le collagène bovin',
          points: SCORES.EXPERT_ORIGIN,
          group: 'preference'
        });
      }


      if (
        pref === 'very_low' &&
        finder.peptideClass === 'very_low'
      ) {
        addScore(result, {
          code: 'expert_very_low',
          label: 'Très faible taille de peptides recherchée',
          points: SCORES.EXPERT_PEPTIDES,
          group: 'preference'
        });
      }


      if (
        pref === 'simple' &&
        !answers.formulaPreference &&
        formulaStyle === 'simple'
      ) {
        addScore(result, {
          code: 'expert_simple',
          label: 'Formule essentiellement composée de collagène',
          points: SCORES.EXPERT_FORMULA,
          group: 'preference'
        });
      }


      if (
        pref === 'enriched' &&
        !answers.formulaPreference
      ) {
        if (formulaStyle === 'complete') {
          addScore(result, {
            code: 'expert_enriched',
            label: 'Formule enrichie recherchée',
            points: SCORES.EXPERT_FORMULA,
            group: 'preference'
          });
        }

        else if (formulaStyle === 'balanced') {
          addScore(result, {
            code: 'expert_enriched_partial',
            label: 'Formule avec actifs complémentaires',
            points: SCORES.EXPERT_FORMULA_PARTIAL,
            group: 'preference'
          });
        }
      }
    }


    /* ----------------------------------------------------------
       10. PRÉFÉRENCE D'ORIGINE
       ---------------------------------------------------------- */

   /* ----------------------------------------------------------
   10. PRÉFÉRENCE D'ORIGINE
   Une préférence explicite doit réellement influencer
   le classement, sans devenir une exclusion absolue.
   ---------------------------------------------------------- */

if (
  answers.originPreference &&
  answers.originPreference !== 'none'
) {

  if (finder.origin === answers.originPreference) {

    addScore(result, {
      code: 'origin_match',
      label: `Origine préférée : ${answers.originPreference}`,
      points: SCORES.ORIGIN_MATCH,
      group: 'preference'
    });

  }

  else if (finder.origin) {

    addScore(result, {
      code: 'origin_mismatch',
      label: `Origine différente de votre préférence : ${finder.origin}`,
      points: SCORES.ORIGIN_MISMATCH,
      group: 'preference'
    });

    addMismatch(
      result,
      'origin',
      `Origine différente de votre préférence (${answers.originPreference}).`
    );

  }

}


    /* ----------------------------------------------------------
       11. NIVEAU D'ACTIVITÉ
       ---------------------------------------------------------- */

    if (
      answers.activityLevel &&
      includes(
        finder.activityLevels,
        answers.activityLevel
      )
    ) {
      addScore(result, {
        code: 'activity_level',
        label: `Niveau d’activité : ${answers.activityLevel}`,
        points: SCORES.ACTIVITY_LEVEL,
        group: 'context'
      });
    }


    /* ----------------------------------------------------------
       12. TYPE DE SPORT
       ---------------------------------------------------------- */

    if (
      answers.sportType &&
      includes(
        finder.sportTypes,
        answers.sportType
      )
    ) {
      addScore(result, {
        code: 'sport_type',
        label: `Activité sportive : ${answers.sportType}`,
        points: SCORES.SPORT_TYPE,
        group: 'context'
      });
    }


    /* ----------------------------------------------------------
       13. CERTIFICATION SPORTIVE
       ---------------------------------------------------------- */

    if (
      answers.antidopingPreference === 'yes' &&
      isTrue(finder.antidopingCertified)
    ) {
      addScore(result, {
        code: 'antidoping',
        label: 'Certification sportive recherchée',
        points: SCORES.ANTIDOPING,
        group: 'context'
      });
    }


    /* ----------------------------------------------------------
       14. SPÉCIFICITÉ CHEVEUX
       ---------------------------------------------------------- */

    if (
      answers.mainGoal === 'hair' &&
      answers.hairFormulaPreference === 'specific' &&
      isTrue(finder.hairSpecific)
    ) {
      addScore(result, {
        code: 'hair_specific',
        label: 'Formule spécifiquement enrichie pour les cheveux',
        points: SCORES.HAIR_SPECIFIC,
        group: 'context'
      });
    }


    /* ----------------------------------------------------------
       15. PRATICITÉ
       ---------------------------------------------------------- */

    if (
      answers.practicality &&
      answers.practicality !== 'none' &&
      includes(
        finder.practicality,
        answers.practicality
      )
    ) {
      addScore(result, {
        code: 'practicality',
        label: `Préférence quotidienne : ${answers.practicality}`,
        points: SCORES.PRACTICALITY,
        group: 'preference'
      });
    }


    /* ----------------------------------------------------------
       16. AUTRES COMPLÉMENTS
       ---------------------------------------------------------- */

    if (
      formulaStyle === 'simple' &&
      answers.supplements === 'many'
    ) {
      addScore(result, {
        code: 'supplements_many',
        label: 'Formule simple privilégiée avec plusieurs compléments déjà consommés',
        points: SCORES.SUPPLEMENTS_MANY,
        group: 'preference'
      });
    }

    else if (
      formulaStyle === 'simple' &&
      answers.supplements === 'some'
    ) {
      addScore(result, {
        code: 'supplements_some',
        label: 'Formule simple légèrement favorisée',
        points: SCORES.SUPPLEMENTS_SOME,
        group: 'preference'
      });
    }


    return result;
  }


  /* ==========================================================
     TRI / DÉPARTAGE
     ========================================================== */

  function sortResults(a, b) {

    /* 1. Score total */
    if (b.score !== a.score) {
      return b.score - a.score;
    }

    /* 2. Critères fondamentaux */
    if (b.coreScore !== a.coreScore) {
      return b.coreScore - a.coreScore;
    }

    /* 3. Nombre de correspondances positives */
    if (b.positiveMatches !== a.positiveMatches) {
      return b.positiveMatches - a.positiveMatches;
    }

    /* 4. Pas de favoritisme marque :
       ordre alphabétique uniquement pour stabilité technique */
    return String(a.product.title || '')
      .localeCompare(String(b.product.title || ''), 'fr');
  }


  /* ==========================================================
     ALTERNATIVES
     ========================================================== */

  function selectRecommendations(pool) {

    if (!pool.length) {
      return [];
    }

    const top = pool[0];

    const recommendations = [
      {
        ...top,
        rank: 1
      }
    ];


    if (top.score <= 0) {
      return recommendations;
    }


    const second = pool[1];

    if (
      second &&
      second.positiveMatches >= 2 &&
      second.score >= top.score * 0.35
    ) {
      recommendations.push({
        ...second,
        rank: 2
      });
    }


    const third = pool[2];

    if (
      recommendations.length >= 2 &&
      third &&
      third.positiveMatches >= 2 &&
      third.score >= top.score * 0.20
    ) {
      recommendations.push({
        ...third,
        rank: 3
      });
    }


    return recommendations;
  }


  /* ==========================================================
     CLASSEMENT GLOBAL
     ========================================================== */

  function rankProducts(products = [], rawAnswers = {}) {

    const answers =
      normalizeAnswers(rawAnswers);


    const scored = products
      .map((product) =>
        scoreProduct(product, answers)
      );


    /* Les exclusions absolues ne peuvent pas être contournées. */
    const safeProducts = scored
      .filter((result) => !result.excluded);


    if (!safeProducts.length) {
      return {
        status: 'no_safe_product',
        partialMatch: true,
        recommendations: [],
        allResults: scored,
        answers
      };
    }


    /* On privilégie toujours les produits compatibles
       avec l'objectif principal. */
    const mainGoalMatches = safeProducts
      .filter(
        (result) =>
          result.mainGoalCompatible
      );


    let pool;
    let partialMatch = false;


    if (mainGoalMatches.length > 0) {
      pool = mainGoalMatches;
    }

    else {
      /*
        Cas demandé :
        aucun produit ne correspond parfaitement à
        l'objectif principal.

        On recommande tout de même le meilleur produit
        restant, sans contourner une allergie/exclusion.
      */
      pool = safeProducts;
      partialMatch = true;
    }


    pool.sort(sortResults);


    const recommendations =
      selectRecommendations(pool);


    return {
      status:
        partialMatch
          ? 'partial_match'
          : 'ok',

      partialMatch,

      recommendations,

      allResults: scored
        .sort(sortResults),

      answers
    };
  }


  /* ==========================================================
     API PUBLIQUE
     ========================================================== */

  window.ProductFinderEngine = {
    SCORES,
    normalizeAnswers,
    scoreProduct,
    rankProducts
  };

})();